import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLaunchState } from "@/components/Countdown";
import Countdown from "@/components/Countdown";
import { Sparkles, Clock, Flame, X } from "lucide-react";
import api from "@/lib/api";

/**
 * Slim sticky launch bar. Always shows:
 *   • Founding presale message OR pre-launch countdown (based on launch state)
 *   • Live seat-count from /launch/founding-count (refreshes every 60s)
 * Sticks to top-of-viewport so scarcity stays in view.
 *
 * Dismiss behaviour: after the user has SEEN the banner 3 times (across
 * page loads / sessions — tracked in localStorage), a small X appears that
 * lets them close it for the current session.
 */
const VIEW_KEY = "ff2d_launch_banner_views_v1";
const DISMISS_KEY = "ff2d_launch_banner_dismissed_v1";
const VIEWS_TO_ENABLE_DISMISS = 3;

export default function LaunchBanner() {
  const state = useLaunchState();
  const [seats, setSeats] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [views, setViews] = useState(0);

  // Track cumulative views across sessions (localStorage) so dismiss unlocks
  // after the user has genuinely seen the bar N times.
  useEffect(() => {
    try {
      const n = parseInt(localStorage.getItem(VIEW_KEY) || "0", 10) + 1;
      localStorage.setItem(VIEW_KEY, String(n));
      setViews(n);
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetch = () => api.get("/launch/founding-count")
      .then(r => { if (!cancelled) setSeats(r.data); })
      .catch(() => {});
    fetch();
    const id = setInterval(fetch, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!state) return null;
  if (dismissed) return null;

  const canDismiss = views >= VIEWS_TO_ENABLE_DISMISS;
  const doDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
  };

  const SeatBadge = () => {
    if (!seats || seats.is_full) return null;
    const remaining = seats.remaining ?? 0;
    const urgent = remaining <= 50;
    return (
      <span
        className={`hidden md:inline-flex items-center gap-1 font-semibold tabular-nums ${
          urgent ? "text-[var(--ff-citrus)]" : ""
        }`}
        data-testid="launch-banner-seats"
      >
        <Flame size={13} className={urgent ? "animate-pulse" : ""}/> {remaining} seats left
      </span>
    );
  };

  const DismissBtn = ({ tone }) => {
    if (!canDismiss) return null;
    return (
      <button
        type="button"
        onClick={doDismiss}
        aria-label="Dismiss launch banner for this session"
        data-testid="launch-banner-dismiss"
        className={`ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full transition-colors ${
          tone === "dark"
            ? "text-white/60 hover:text-white hover:bg-white/15"
            : "text-[var(--ff-text-muted)] hover:text-[var(--ff-primary)] hover:bg-white/50"
        }`}
      >
        <X size={12} />
      </button>
    );
  };

  // Common sticky wrapper
  const wrap = (kind, tone, inner, isDark = false) => (
    <div
      data-testid={`launch-banner-${kind}`}
      className={`w-full ${tone}`}
    >
      <div className="max-w-7xl mx-auto px-6 py-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs sm:text-sm">
        {inner}
        <DismissBtn tone={isDark ? "dark" : "light"} />
      </div>
    </div>
  );

  if (state.presale?.active) {
    return wrap("presale", "bg-[var(--ff-primary)] text-white", (
      <>
        <span className="inline-flex items-center gap-1.5 font-semibold">
          <Sparkles size={14} /> Founding presale is live
        </span>
        <SeatBadge />
        <span className="opacity-75">Ends in</span>
        <Countdown to={state.presale.end} compact data-testid="presale-countdown"/>
        <Link to="/pricing" className="underline underline-offset-4 hover:opacity-80">Lock yours in →</Link>
      </>
    ), true);
  }

  if (state.july4_flash?.active) {
    return wrap("july4", "bg-[var(--ff-citrus)] text-[var(--ff-text-primary)]", (
      <>
        <span className="font-semibold">🎆 July 4 Flash · 50% off Premium Annual — today only</span>
        <span className="opacity-75">Ends in</span>
        <Countdown to={state.july4_flash.end} compact />
        <Link to="/pricing" className="underline underline-offset-4">Grab it →</Link>
      </>
    ));
  }

  if (state.launch_at && state.phase !== "launched") {
    return wrap("prelaunch", "bg-[var(--ff-muted)] border-b border-[var(--ff-border)] text-[var(--ff-text-secondary)]", (
      <>
        <span className="inline-flex items-center gap-1.5">
          <Clock size={13} className="text-[var(--ff-primary)]"/>
          Drops launch in
        </span>
        <span className="font-semibold text-[var(--ff-primary)] tabular-nums" data-testid="prelaunch-countdown-wrap">
          <Countdown to={state.launch_at} compact />
        </span>
        <SeatBadge />
        <Link to="/pricing" className="hidden sm:inline underline underline-offset-4 hover:text-[var(--ff-primary)]">
          Become a member →
        </Link>
      </>
    ));
  }

  return null;
}
